// Models
import cloneDeep from "lodash/cloneDeep";
import { Temporal } from "temporal-polyfill-lite";
import { PatchableAndDeltaSyncableODataEntity } from "../common/PatchableAndDeltaSyncableODataEntity";
import { CustomFieldChoice } from "./CustomFieldChoice";
/**
 * The custom field definition entity implementation.
 */ export class CustomFieldDefinition extends PatchableAndDeltaSyncableODataEntity {
    getPropertyMappingOverride() {
        return {
            choices: "choices@delta"
        };
    }
    mapEntityChangesToResource(changed, source) {
        const resourceKeyMap = this.getChangePropertyMapping();
        return {
            [resourceKeyMap.displayName]: changed.displayName,
            [resourceKeyMap.rollupType]: changed.rollupType,
            [resourceKeyMap.choices]: this.processSubEntityArray(this.choices, source.choices)
        };
    }
    mapDeltaChangesToEntity(delta) {
        return this.processDeltaWithTranslators(delta, {
            archivalInfo: (value)=>({
                    ...value,
                    modifiedDate: value.modifiedDate ? Temporal.PlainDate.from(value.modifiedDate) : null
                }),
            choices: (deltaValue)=>this.applySubEntityArrayDelta(deltaValue, this.choices),
            creationInfo: (value)=>({
                    ...value,
                    createdDateTime: Temporal.Instant.from(value.createdDateTime)
                })
        });
    }
    static fromResource(resource) {
        return new CustomFieldDefinition({
            id: resource.id,
            version: resource["@etag"],
            archivalInfo: {
                ...resource.archivalInfo,
                isArchived: resource.archivalInfo?.isArchived ?? false,
                justification: resource.archivalInfo?.justification ?? null,
                modifiedBy: resource.archivalInfo?.modifiedBy ?? null,
                modifiedDate: resource.archivalInfo?.modifiedDate ? Temporal.PlainDate.from(resource.archivalInfo.modifiedDate) : null
            },
            choices: resource.choices?.map((choice)=>CustomFieldChoice.fromResource(choice)) ?? [],
            creationInfo: {
                ...resource.creationInfo,
                createdBy: resource.creationInfo?.createdBy ?? {
                    user: {
                        id: "",
                        tenantId: ""
                    },
                    application: {
                        id: "",
                        tenantId: ""
                    }
                },
                createdDateTime: resource.creationInfo?.createdDateTime ? Temporal.Instant.from(resource.creationInfo.createdDateTime) : Temporal.Now.instant(),
                externalId: resource.creationInfo?.externalId ?? "",
                externalVersion: resource.creationInfo?.externalVersion ?? "",
                scenarioId: resource.creationInfo?.scenarioId ?? "",
                scope: resource.creationInfo?.scope ?? "",
                externalUrl: resource.creationInfo?.externalUrl ?? ""
            },
            displayName: resource.displayName,
            planId: resource.planId,
            valueType: resource.valueType,
            rollupType: resource.rollupType
        });
    }
    getClone() {
        return new CustomFieldDefinition({
            id: this.id,
            version: this.version,
            archivalInfo: {
                ...cloneDeep(this.archivalInfo),
                modifiedDate: this.archivalInfo.modifiedDate ? Temporal.PlainDate.from(this.archivalInfo.modifiedDate) : null
            },
            choices: this.choices.map((choice)=>choice.getClone()),
            creationInfo: {
                ...cloneDeep(this.creationInfo),
                createdDateTime: Temporal.Instant.from(this.creationInfo.createdDateTime)
            },
            displayName: this.displayName,
            planId: this.planId,
            valueType: this.valueType,
            rollupType: this.rollupType
        });
    }
    setProperty(key, value) {
        const clone = this.getClone();
        clone[key] = value;
        return clone;
    }
    forSerialization() {
        return {
            id: this.id,
            "@etag": this.version,
            archivalInfo: {
                isArchived: this.archivalInfo.isArchived,
                justification: this.archivalInfo.justification,
                modifiedBy: this.archivalInfo.modifiedBy,
                modifiedDate: this.archivalInfo.modifiedDate?.toString() ?? null
            },
            choices: this.choices.map((choice)=>choice.forSerialization()),
            creationInfo: {
                createdBy: this.creationInfo.createdBy,
                createdDateTime: this.creationInfo.createdDateTime.toString(),
                externalId: this.creationInfo.externalId,
                externalVersion: this.creationInfo.externalVersion,
                scenarioId: this.creationInfo.scenarioId,
                scope: this.creationInfo.scope,
                externalUrl: this.creationInfo.externalUrl
            },
            displayName: this.displayName,
            planId: this.planId,
            valueType: this.valueType,
            rollupType: this.rollupType
        };
    }
    forCreate() {
        return {
            // choices: this.choices.map((choice) => choice.forCreate()), // Will be added again when service supports creating choices along with definition
            displayName: this.displayName,
            planId: this.planId,
            rollupType: this.rollupType,
            valueType: this.valueType
        };
    }
    constructor(propertyBag){
        super(propertyBag);
        this.archivalInfo = propertyBag.archivalInfo;
        this.choices = propertyBag.choices ?? [];
        this.creationInfo = propertyBag.creationInfo;
        this.displayName = propertyBag.displayName;
        this.planId = propertyBag.planId;
        this.valueType = propertyBag.valueType;
        this.rollupType = propertyBag.rollupType;
    }
}
